// Video Studio — collage video + photo clips into a single 9:16 reel with
// optional per-clip captions and an optional global caption that stays on
// screen the entire time.
//
// Mirrors the Reel Studio UX: upload, reorder, tweak each clip, kick off an
// async render, poll for status, see saved videos. The backend caps the final
// reel at 180s (3 min) — if the sum of clip durations exceeds 180s it scales
// each proportionally so the order/feel is preserved.

import { useEffect, useRef, useState } from "react";
import {
    Loader2,
    Trash2,
    Film,
    ImagePlus,
    Video as VideoIcon,
    ArrowUp,
    ArrowDown,
    Plus,
    RotateCcw,
    Download,
    Volume2,
    VolumeX,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { API, adminClient } from "@/lib/api";
import { uploadVideoTimeline, videoStudioError } from "./videoStudioUpload";
import { MediaDropzone } from "./MediaDropzone";

const FAMILY_OPTS = [
    { v: "sans", t: "Sans" },
    { v: "condensed", t: "Cond" },
    { v: "serif", t: "Serif" },
    { v: "mono", t: "Mono" },
];
const SIZE_OPTS = [
    { v: "S", t: "S" },
    { v: "M", t: "M" },
    { v: "L", t: "L" },
];
const POS_OPTS = [
    { v: "top-left", t: "TL" },
    { v: "top-center", t: "TC" },
    { v: "bottom-left", t: "BL" },
    { v: "bottom-center", t: "BC" },
];
const MOTION_OPTS = [
    { v: "zoom-in", t: "Zoom in" },
    { v: "zoom-out", t: "Zoom out" },
    { v: "static", t: "Static" },
];
const FIT_OPTS = [
    { v: "cover", t: "Fill (crop)" },
    { v: "contain", t: "Whole (blur bg)" },
];
const PRESET_PX = { S: 100, M: 152, L: 216 };

function emptyCaption(pos = "top-left") {
    return { text: "", family: "sans", size: "M", position: pos, sizePx: null };
}

export default function VideoStudio() {
    // clips: ordered timeline. Each: { id, kind, file, previewUrl, caption,
    // motion, fit, duration (image), startSec/endSec (video) }
    const [clips, setClips] = useState([]);
    const [globalCaption, setGlobalCaption] = useState(emptyCaption("top-center"));
    const [rendering, setBusy] = useState(false);
    const [addingMedia, setAddingMedia] = useState(false);
    const addingRef = useRef(false);
    const busy = rendering || addingMedia;
    const [result, setResult] = useState(null);
    const [videos, setVideos] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [renderProgress, setRenderProgress] = useState(null);
    const [renderError, setRenderError] = useState("");
    const photoInputRef = useRef();
    const videoInputRef = useRef();
    // True while we're converting any incoming HEIC → JPG client-side.
    const [convertingHeic, setConvertingHeic] = useState(false);

    const refreshList = async () => {
        setLoadingList(true);
        try {
            const r = await adminClient().get("/admin/videos");
            setVideos(r.data.videos || []);
        } catch {
            // silent — empty list is fine
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => { refreshList(); }, []);

    /**
     * Shrink huge photos client-side before upload. K8s ingress proxies in
     * front of the prod backend reject request bodies bigger than ~100MB as
     * "Network Error", which is what happens when you load 8 lossless PNGs
     * fresh out of the Converter tab (a 12MP iPhone photo as PNG is ~15MB,
     * 8 of them = 120MB). Resizing to a long-edge max of 1920px and
     * re-encoding as JPG 88% is visually identical for our 1080×1920 reel
     * output and ~10× smaller payload.
     */
    const _shrinkIfLarge = async (file) => {
        const SOFT_LIMIT_BYTES = 3 * 1024 * 1024; // 3MB
        if (file.size <= SOFT_LIMIT_BYTES) return file;
        try {
            const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
            const maxEdge = 1920;
            let { width, height } = bitmap;
            const longEdge = Math.max(width, height);
            if (longEdge > maxEdge) {
                const scale = maxEdge / longEdge;
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(bitmap, 0, 0, width, height);
            bitmap.close && bitmap.close();
            const blob = await new Promise((res) =>
                canvas.toBlob(res, "image/jpeg", 0.88),
            );
            if (!blob) return file;
            const newName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
            console.info(
                `[VideoStudio] shrunk ${file.name}: ${(file.size / 1e6).toFixed(1)}MB → ${(blob.size / 1e6).toFixed(1)}MB`,
            );
            return new File([blob], newName, { type: "image/jpeg" });
        } catch (err) {
            console.warn("client shrink failed (using original):", err);
            return file;
        }
    };

    const addMedia = async (incoming, allowedKind = "mixed") => {
        if (busy || addingRef.current || !incoming.length) return;
        addingRef.current = true;
        setAddingMedia(true);
        const hasHeic = incoming.some((f) =>
            /^image\/(heic|heif)/.test(f.type) || /\.hei[cf]$/i.test(f.name),
        );
        if (hasHeic) setConvertingHeic(true);
        const next = [...clips];
        try {
            for (const f of incoming) {
            if (next.length >= 8) {
                toast.error("Max 8 clips total.");
                break;
            }
            if (!f.size) { toast.error(`${f.name}: file is empty`); continue; }
            const isVideo = /^video\/(mp4|quicktime|x-m4v|webm)$/.test(f.type) || /\.(mp4|mov|m4v|webm)$/i.test(f.name);
            if (isVideo && allowedKind !== "image") {
                if (f.size > 80 * 1024 * 1024) { toast.error(`${f.name}: max 80MB per video`); continue; }
                next.push({ id: crypto.randomUUID(), kind: "video", file: f,
                    previewUrl: URL.createObjectURL(f), caption: emptyCaption("top-left"), fit: "cover",
                    startSec: 0, endSec: 0, keepAudio: true, enhance: false, detectedDuration: null });
                continue;
            }
            const isHeic =
                /^image\/(heic|heif)/.test(f.type) || /\.hei[cf]$/i.test(f.name);
            const isOther = /^image\/(png|jpe?g|webp)$/.test(f.type) || (!f.type && /\.(png|jpe?g|webp)$/i.test(f.name));
            if (allowedKind === "video" || (!isHeic && !isOther)) {
                toast.error(`${f.name}: unsupported file. Use PNG/JPG/WebP/HEIC photos or MP4/MOV/M4V/WebM videos.`);
                continue;
            }

            let outFile = f;
            if (isHeic) {
                // Convert HEIC → JPG client-side so the server never sees HEIC
                // (some browsers send empty / wrong content-types for HEIC,
                // which used to trip the type-check and break renders).
                try {
                    const heic2any = (await import("heic2any")).default;
                    const blob = await heic2any({ blob: f, toType: "image/jpeg", quality: 0.9 });
                    const jpgBlob = Array.isArray(blob) ? blob[0] : blob;
                    outFile = new File(
                        [jpgBlob],
                        f.name.replace(/\.hei[cf]$/i, ".jpg"),
                        { type: "image/jpeg" },
                    );
                } catch (err) {
                    console.error("HEIC conversion failed:", err);
                    toast.error(`${f.name}: couldn't convert HEIC — try saving as JPG`);
                    continue;
                }
            }

            // Always run through the shrink pass — it's a no-op for files
            // already under 3MB, so JPGs from a phone camera pass through
            // untouched but giant PNGs get squashed to upload-friendly size.
            outFile = await _shrinkIfLarge(outFile);
            if (outFile.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8MB after photo optimization`); continue; }

            next.push({
                id: crypto.randomUUID(),
                kind: "image",
                file: outFile,
                previewUrl: URL.createObjectURL(outFile),
                isHeic: false, // post-conversion the file IS jpg
                caption: emptyCaption("top-left"),
                motion: "zoom-in",
                fit: "cover",
                duration: 2.5,
            });
            }
            const additions = next.slice(clips.length);
            setClips(current => [...current, ...additions]);
            if (photoInputRef.current) photoInputRef.current.value = "";
        } finally {
            setConvertingHeic(false);
            setAddingMedia(false);
            addingRef.current = false;
        }
    };

    const pickMedia = (event, kind) => {
        const incoming = Array.from(event.target.files || []);
        event.target.value = "";
        addMedia(incoming, kind);
    };

    const removeClip = (id) => setClips((cs) => cs.filter((c) => { if (c.id !== id) return true; URL.revokeObjectURL(c.previewUrl); return false; }));
    const moveClip = (id, dir) => {
        setClips((cs) => {
            const i = cs.findIndex((c) => c.id === id);
            const j = i + dir;
            if (i < 0 || j < 0 || j >= cs.length) return cs;
            const out = [...cs];
            [out[i], out[j]] = [out[j], out[i]];
            return out;
        });
    };
    const updateClip = (id, patch) =>
        setClips((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    const updateCaption = (id, patch) =>
        setClips((cs) =>
            cs.map((c) => (c.id === id ? { ...c, caption: { ...c.caption, ...patch } } : c)),
        );

    // Video metadata probe (client-side) so we can show duration and clamp trim sliders.
    const probeVideoDuration = (id, previewUrl) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => {
            if (Number.isFinite(v.duration) && v.duration > 0) {
                updateClip(id, { detectedDuration: Number(v.duration.toFixed(2)) });
            }
        };
        v.src = previewUrl;
    };
    useEffect(() => {
        for (const c of clips) {
            if (c.kind === "video" && c.detectedDuration == null) {
                probeVideoDuration(c.id, c.previewUrl);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clips.length]);

    const totalEstimated = (() => {
        let s = 0;
        for (const c of clips) {
            if (c.kind === "image") s += Math.max(0.5, Math.min(60, c.duration || 2.5));
            else {
                const start = c.startSec || 0;
                const end = c.endSec || c.detectedDuration || 5;
                s += Math.max(0.5, Math.min(120, (end - start) || 5));
            }
        }
        return s;
    })();
    const cappedAt180 = totalEstimated > 180;

    const generate = async () => {
        if (busy) return;
        if (clips.length < 1) return toast.error("Add at least 1 clip.");
        if (clips.length > 8) return toast.error("Max 8 clips.");

        setBusy(true);
        setResult(null);
        setRenderError("");

        // Keep photo optimization; original video bytes upload individually.
        setRenderProgress({ phase: "Optimizing photos…", current: 0, total: clips.length });
        let workingClips = clips;
        try {
            workingClips = await Promise.all(
                clips.map(async (c) => {
                    if (c.kind !== "image") return c;
                    const shrunk = await _shrinkIfLarge(c.file);
                    if (shrunk === c.file) return c;
                    return { ...c, file: shrunk };
                }),
            );
        } catch (err) {
            console.warn("optimize pass failed (using originals):", err);
        }

        let jobId;
        try {
            jobId = await uploadVideoTimeline(workingClips, globalCaption, setRenderProgress);
        } catch (e) {
            const message = videoStudioError(e);
            setRenderError(message);
            toast.error(message, { duration: 18000 });
            setBusy(false);
            setRenderProgress(null);
            return;
        }

        const started = Date.now();
        // Slightly longer than the backend's max overall timeout (max(900, n*240))
        // so the backend's clean "failed" state always lands here first.
        const MAX_WAIT = 40 * 60 * 1000;
        let notFound = 0;
        try {
            await new Promise((r) => setTimeout(r, 1500));
            while (Date.now() - started < MAX_WAIT) {
                try {
                    const sres = await adminClient().get(`/admin/videos/${jobId}/status`, { timeout: 15000 });
                    const data = sres.data || {};
                    notFound = 0;
                    if (data.status === "done") {
                        setResult(data);
                        toast.success("Video collage rendered.");
                        refreshList();
                        setBusy(false);
                        setRenderProgress(null);
                        return;
                    }
                    if (data.status === "failed") {
                        const message = `Render failed: ${data.error || "Video processing could not finish."} Reference: ${jobId}`;
                        setRenderError(message);
                        toast.error(message, { duration: 15000 });
                        setBusy(false);
                        setRenderProgress(null);
                        return;
                    }
                    if (data.phase) {
                        setRenderProgress({
                            phase: data.phase,
                            current: data.progress_current ?? 0,
                            total: data.progress_total ?? clips.length,
                        });
                    }
                } catch (e) {
                    if ([401, 403].includes(e?.response?.status)) { setRenderError(videoStudioError(e)); return; }
                    if (e?.response?.status === 404) {
                        notFound += 1;
                        // ~70s of grace after a hot-reload / pod restart before
                        // giving up. The backend now auto-resumes on startup,
                        // so real "job lost" is extremely rare.
                        if (notFound >= 20) {
                            const message = `Render status is temporarily unavailable. Check Saved Videos before retrying. Reference: ${jobId}`;
                            setRenderError(message);
                            toast.error(message, { duration: 15000 });
                            setBusy(false);
                            setRenderProgress(null);
                            return;
                        }
                    }
                }
                await new Promise((r) => setTimeout(r, 3000));
            }
            const message = `Render is still pending. Check Saved Videos shortly. Reference: ${jobId}`;
            setRenderError(message);
            toast.error(message, { duration: 15000 });
        } finally {
            setBusy(false);
            setRenderProgress(null);
        }
    };

    const deleteVideo = async (id) => {
        if (!window.confirm("Delete this video?")) return;
        try {
            await adminClient().delete(`/admin/videos/${id}`);
            toast.success("Deleted.");
            if (result?.id === id) setResult(null);
            refreshList();
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    const fullUrl = (path) => `${API.replace(/\/api$/, "")}${path}`;

    return (
        <div className="grid min-w-0 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 mt-6" data-testid="video-studio">
            <div className="min-w-0 space-y-6">
                <div className="rounded-sm border border-white/10 bg-zinc-900 p-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h3 className="font-display font-black uppercase tracking-tight text-white text-xl">
                                Video Studio
                            </h3>
                            <p className="text-xs text-zinc-400 mt-1">
                                Mix up to 8 videos + photos into one 9:16 reel (max 3 min).
                                Each clip can have its own caption — plus an optional global caption that stays on screen the whole time.
                            </p>
                        </div>
                        {clips.length > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (window.confirm("Clear all clips?")) { clips.forEach(c => URL.revokeObjectURL(c.previewUrl)); setClips([]); }
                                }}
                                disabled={busy}
                                data-testid="video-studio-reset"
                                className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white border border-white/15 px-2.5 py-1 rounded-sm"
                            >
                                <RotateCcw className="w-3 h-3" /> Clear all
                            </button>
                        )}
                    </div>

                    <div className="mt-4">
                        <MediaDropzone prefix="video-studio" title="Drop photos & videos here, or browse"
                            hint="Up to 8 clips · PNG/JPG/WebP/HEIC · MP4/MOV/M4V/WebM · 80MB per video"
                            accept="image/png,image/jpeg,image/webp,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/x-m4v,video/webm,.mp4,.mov,.m4v,.webm"
                            disabled={busy || clips.length >= 8} preparing={addingMedia} onFiles={addMedia} />
                    </div>
                    {/* Add buttons */}
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => photoInputRef.current?.click()}
                            disabled={busy || clips.length >= 8}
                            data-testid="video-studio-add-photos"
                            className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white text-white text-sm px-3 py-2 rounded-sm disabled:opacity-60"
                        >
                            {convertingHeic ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Converting HEIC…
                                </>
                            ) : (
                                <>
                                    <ImagePlus className="w-4 h-4" /> Add photos
                                </>
                            )}
                        </button>
                        <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/heic,image/heif,.heic,.heif"
                            multiple
                            disabled={busy || clips.length >= 8}
                            onChange={event => pickMedia(event, "image")}
                            className="hidden"
                            data-testid="video-studio-photo-input"
                        />
                        <button
                            type="button"
                            onClick={() => videoInputRef.current?.click()}
                            disabled={busy || clips.length >= 8}
                            data-testid="video-studio-add-videos"
                            className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white text-white text-sm px-3 py-2 rounded-sm"
                        >
                            <VideoIcon className="w-4 h-4" /> Add videos
                        </button>
                        <input
                            ref={videoInputRef}
                            type="file"
                            accept="video/mp4,video/quicktime,video/x-m4v,video/webm"
                            multiple
                            disabled={busy || clips.length >= 8}
                            onChange={event => pickMedia(event, "video")}
                            className="hidden"
                            data-testid="video-studio-video-input"
                        />
                        <div data-testid="video-studio-clip-count" className="ml-auto text-[11px] text-zinc-400 tabular-nums">
                            {clips.length}/8 clips · est. {totalEstimated.toFixed(1)}s
                            {cappedAt180 && (
                                <span className="text-amber-400 ml-1">
                                    (will be scaled to 3 min)
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Global caption */}
                <fieldset disabled={busy} className="min-w-0 rounded-sm border border-cyan-700/40 bg-zinc-900 p-4 disabled:opacity-70">
                    <div className="text-xs uppercase tracking-widest text-cyan-400/80 font-bold mb-2">
                        Global Caption · stays on the whole reel
                    </div>
                    <textarea
                        value={globalCaption.text}
                        onChange={(e) =>
                            setGlobalCaption({ ...globalCaption, text: e.target.value.slice(0, 200) })
                        }
                        rows={2}
                        placeholder="Optional caption shown over every clip (e.g. 'BEFORE & AFTER')"
                        data-testid="video-studio-global-caption"
                        className="w-full bg-zinc-950 border border-white/10 rounded-sm px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 resize-none focus:outline-none focus:border-cyan-500"
                    />
                    <CaptionControls
                        cap={globalCaption}
                        onChange={(patch) => setGlobalCaption({ ...globalCaption, ...patch })}
                        prefix="video-studio-global"
                    />
                </fieldset>

                {/* Clips list */}
                <fieldset disabled={busy} className="min-w-0 space-y-3 disabled:opacity-70">
                    {clips.length === 0 && (
                        <div className="rounded-sm border border-dashed border-white/15 bg-zinc-950 p-10 text-center text-zinc-500 text-sm">
                            No clips yet — add some photos or videos to get started.
                        </div>
                    )}
                    {clips.map((c, i) => (
                        <div
                            key={c.id}
                            className="min-w-0 rounded-sm border border-white/10 bg-zinc-950 p-3 grid grid-cols-[72px_minmax(0,1fr)] sm:grid-cols-[88px_minmax(0,1fr)] gap-3"
                            data-testid={`video-studio-clip-${i}`}
                        >
                            <div className="relative">
                                {c.kind === "video" ? (
                                    <video
                                        src={c.previewUrl}
                                        muted
                                        className="w-full aspect-[9/16] object-cover rounded-sm border border-white/10 bg-black"
                                    />
                                ) : c.isHeic || !c.previewUrl ? (
                                    <div className="w-full aspect-[9/16] rounded-sm border border-white/10 bg-zinc-900 flex flex-col items-center justify-center text-center px-1">
                                        <ImagePlus className="w-5 h-5 text-zinc-500 mb-1" />
                                        <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold">HEIC</span>
                                        <span className="text-[9px] text-zinc-500 mt-0.5 leading-tight">converts on render</span>
                                    </div>
                                ) : (
                                    <img
                                        src={c.previewUrl}
                                        alt=""
                                        className="w-full aspect-[9/16] object-cover rounded-sm border border-white/10"
                                    />
                                )}
                                <span className="absolute top-1 left-1 text-[10px] font-bold text-white bg-black/70 px-1.5 py-0.5 rounded-sm">
                                    #{i + 1}
                                </span>
                                <span className={`absolute bottom-1 left-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                                    c.kind === "video"
                                        ? "bg-purple-600/80 text-white"
                                        : "bg-zinc-700/80 text-white"
                                }`}>
                                    {c.kind}
                                </span>
                            </div>
                            <div className="min-w-0 space-y-2">
                                <textarea
                                    value={c.caption.text}
                                    onChange={(e) => updateCaption(c.id, { text: e.target.value.slice(0, 280) })}
                                    rows={2}
                                    placeholder="Caption for this clip (optional)"
                                    data-testid={`video-studio-clip-caption-${i}`}
                                    className="w-full bg-zinc-900 border border-white/10 rounded-sm px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600 resize-none"
                                />
                                <CaptionControls
                                    cap={c.caption}
                                    onChange={(patch) => updateCaption(c.id, patch)}
                                    prefix={`video-studio-clip-${i}`}
                                />
                                <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center pt-1">
                                    <label className="inline-flex items-center gap-1.5">
                                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Fit</span>
                                        <select
                                            value={c.fit}
                                            onChange={(e) => updateClip(c.id, { fit: e.target.value })}
                                            data-testid={`video-studio-clip-fit-${i}`}
                                            className="bg-zinc-900 border border-white/10 text-zinc-200 text-[11px] rounded-sm px-2 py-1 focus:outline-none focus:border-red-600"
                                        >
                                            {FIT_OPTS.map((o) => (
                                                <option key={o.v} value={o.v} className="bg-zinc-900">{o.t}</option>
                                            ))}
                                        </select>
                                    </label>
                                    {c.kind === "image" && (
                                        <>
                                            <label className="inline-flex items-center gap-1.5">
                                                <span className="text-[10px] uppercase tracking-widest text-cyan-400/80">Motion</span>
                                                <select
                                                    value={c.motion || "zoom-in"}
                                                    onChange={(e) => updateClip(c.id, { motion: e.target.value })}
                                                    data-testid={`video-studio-clip-motion-${i}`}
                                                    className="bg-zinc-900 border border-cyan-700/40 text-cyan-200 text-[11px] rounded-sm px-2 py-1 focus:outline-none focus:border-cyan-500"
                                                >
                                                    {MOTION_OPTS.map((o) => (
                                                        <option key={o.v} value={o.v} className="bg-zinc-900">{o.t}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="inline-flex items-center gap-1.5">
                                                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Sec</span>
                                                <input
                                                    type="number"
                                                    min={0.5}
                                                    max={60}
                                                    step={0.5}
                                                    value={c.duration}
                                                    onChange={(e) => updateClip(c.id, { duration: Math.max(0.5, Math.min(60, Number(e.target.value) || 2.5)) })}
                                                    data-testid={`video-studio-clip-duration-${i}`}
                                                    className="w-16 bg-zinc-900 border border-white/10 text-zinc-200 text-[11px] rounded-sm px-2 py-1 focus:outline-none focus:border-red-600"
                                                />
                                            </label>
                                        </>
                                    )}
                                    {c.kind === "video" && (
                                        <>
                                            <label className="inline-flex items-center gap-1.5">
                                                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Start</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={0.1}
                                                    value={c.startSec || 0}
                                                    onChange={(e) => updateClip(c.id, { startSec: Math.max(0, Number(e.target.value) || 0) })}
                                                    data-testid={`video-studio-clip-start-${i}`}
                                                    className="w-16 bg-zinc-900 border border-white/10 text-zinc-200 text-[11px] rounded-sm px-2 py-1"
                                                />
                                            </label>
                                            <label className="inline-flex items-center gap-1.5">
                                                <span className="text-[10px] uppercase tracking-widest text-zinc-500">End</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={0.1}
                                                    placeholder={c.detectedDuration ? c.detectedDuration.toFixed(1) : "full"}
                                                    value={c.endSec || ""}
                                                    onChange={(e) => updateClip(c.id, { endSec: Math.max(0, Number(e.target.value) || 0) })}
                                                    data-testid={`video-studio-clip-end-${i}`}
                                                    className="w-16 bg-zinc-900 border border-white/10 text-zinc-200 text-[11px] rounded-sm px-2 py-1"
                                                />
                                            </label>
                                            {c.detectedDuration && (
                                                <span className="text-[10px] text-zinc-500">
                                                    src {c.detectedDuration.toFixed(1)}s
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => updateClip(c.id, { keepAudio: !(c.keepAudio !== false) })}
                                                data-testid={`video-studio-clip-audio-${i}`}
                                                title={c.keepAudio !== false ? "Audio ON — click to mute this clip" : "Muted — click to include audio"}
                                                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border rounded-sm px-2 py-1 ${
                                                    c.keepAudio !== false
                                                        ? "border-emerald-600/60 bg-emerald-600/10 text-emerald-300 hover:bg-emerald-600/20"
                                                        : "border-zinc-700 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                                                }`}
                                            >
                                                {c.keepAudio !== false ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                                                {c.keepAudio !== false ? "Audio on" : "Muted"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => updateClip(c.id, { enhance: !c.enhance })}
                                                data-testid={`video-studio-clip-enhance-${i}`}
                                                title={c.enhance ? "Enhancement ON — denoise + sharpen + color boost. Adds render time." : "Click to enhance this clip's quality"}
                                                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border rounded-sm px-2 py-1 ${
                                                    c.enhance
                                                        ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/20"
                                                        : "border-zinc-700 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                                                }`}
                                            >
                                                <Sparkles className="w-3 h-3" />
                                                {c.enhance ? "Enhance on" : "Enhance"}
                                            </button>
                                        </>
                                    )}

                                    <div className="ml-auto inline-flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => moveClip(c.id, -1)}
                                            data-testid={`video-studio-clip-up-${i}`}
                                            className="text-zinc-400 hover:text-white p-1"
                                            title="Move up"
                                        ><ArrowUp className="w-3.5 h-3.5" /></button>
                                        <button
                                            type="button"
                                            onClick={() => moveClip(c.id, 1)}
                                            data-testid={`video-studio-clip-down-${i}`}
                                            className="text-zinc-400 hover:text-white p-1"
                                            title="Move down"
                                        ><ArrowDown className="w-3.5 h-3.5" /></button>
                                        <button
                                            type="button"
                                            onClick={() => removeClip(c.id)}
                                            data-testid={`video-studio-clip-remove-${i}`}
                                            className="text-zinc-400 hover:text-red-500 p-1"
                                            title="Remove"
                                        ><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </fieldset>

                {renderProgress && <div className="space-y-2" role="status" aria-live="polite" data-testid="video-studio-progress">
                    <p className="break-words text-xs text-zinc-300" data-testid="video-studio-progress-label">{renderProgress.phase}</p>
                    {!!renderProgress.totalBytes && <>
                        <progress max={renderProgress.totalBytes} value={renderProgress.bytes || 0} data-testid="video-studio-upload-progress" className="h-2 w-full accent-red-600" />
                        <p data-testid="video-studio-upload-bytes" className="text-xs text-zinc-400">{((renderProgress.bytes || 0) / 1e6).toFixed(1)} / {(renderProgress.totalBytes / 1e6).toFixed(1)} MB saved or transferring</p>
                    </>}
                </div>}
                {renderError && <div role="alert" data-testid="video-studio-error" className="break-words border-l-2 border-red-500 pl-3 text-sm text-red-300">{renderError}</div>}

                <button
                    type="button"
                    onClick={generate}
                    disabled={busy || clips.length < 1}
                    data-testid="video-studio-generate"
                    className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-sm flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
                    {addingMedia ? "Preparing media…" : busy
                        ? (renderProgress?.phase
                            ? `${renderProgress.phase}${renderProgress.total ? ` (${renderProgress.current || 0}/${renderProgress.total})` : ""}`
                            : "Starting render…")
                        : "Generate video collage"}
                </button>
            </div>

            {/* Side rail: result + saved videos */}
            <div className="min-w-0 space-y-4">
                {result && (
                    <div className="rounded-sm border border-green-700/40 bg-zinc-900 p-4">
                        <h4 className="font-display font-bold uppercase tracking-tight text-white text-sm">
                            Latest render
                        </h4>
                        <video
                            src={fullUrl(result.url)}
                            controls
                            playsInline
                            data-testid="video-studio-latest-video"
                            className="mt-2 w-full aspect-[9/16] bg-black rounded-sm"
                        />
                        <a
                            href={fullUrl(result.url)}
                            download={`${result.id}.mp4`}
                            data-testid="video-studio-latest-download"
                            className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-200 hover:text-white border border-white/20 px-3 py-1.5 rounded-sm"
                        >
                            <Download className="w-3.5 h-3.5" /> Download MP4
                        </a>
                    </div>
                )}

                <div className="rounded-sm border border-white/10 bg-zinc-900 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-display font-bold uppercase tracking-tight text-white text-sm">
                            Saved Videos
                        </h4>
                        <span className="text-[10px] text-zinc-500">
                            {loadingList ? "…" : `${videos.length} on file`}
                        </span>
                    </div>
                    <div className="space-y-2">
                        {videos.length === 0 && !loadingList && (
                            <p className="text-xs text-zinc-500">Nothing here yet.</p>
                        )}
                        {videos.map((v) => (
                            <div
                                key={v.id}
                                className="flex items-center justify-between gap-2 rounded-sm border border-white/10 bg-zinc-950 p-2"
                                data-testid={`video-studio-saved-${v.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="text-xs text-white truncate">{v.id}</div>
                                    <div className="text-[10px] text-zinc-500">
                                        {(v.bytes / (1024 * 1024)).toFixed(1)} MB ·{" "}
                                        {new Date(v.created_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <a
                                        href={fullUrl(v.url)}
                                        download={`${v.id}.mp4`}
                                        data-testid={`video-studio-saved-download-${v.id}`}
                                        className="text-[10px] text-zinc-300 hover:text-white border border-white/20 px-2 py-1 rounded-sm"
                                    >Download</a>
                                    <button
                                        type="button"
                                        onClick={() => deleteVideo(v.id)}
                                        data-testid={`video-studio-saved-delete-${v.id}`}
                                        className="text-zinc-400 hover:text-red-500 p-1"
                                    ><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function CaptionControls({ cap, onChange, prefix }) {
    const activePx = cap.sizePx || PRESET_PX[cap.size] || 152;
    const pctOfWidth = ((activePx / 1080) * 100).toFixed(1);
    return (
        <div className="space-y-1.5">
            <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center">
                <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Font</span>
                    {FAMILY_OPTS.map((o) => (
                        <button
                            key={o.v}
                            type="button"
                            onClick={() => onChange({ family: o.v })}
                            data-testid={`${prefix}-font-${o.v}`}
                            className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${
                                cap.family === o.v
                                    ? "bg-red-600/20 border-red-600 text-white"
                                    : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                        >{o.t}</button>
                    ))}
                </div>
                <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Size</span>
                    {SIZE_OPTS.map((o) => (
                        <button
                            key={o.v}
                            type="button"
                            onClick={() => onChange({ size: o.v, sizePx: null })}
                            data-testid={`${prefix}-size-${o.v.toLowerCase()}`}
                            className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${
                                !cap.sizePx && cap.size === o.v
                                    ? "bg-red-600/20 border-red-600 text-white"
                                    : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                        >{o.t}</button>
                    ))}
                </div>
                <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Pos</span>
                    {POS_OPTS.map((o) => (
                        <button
                            key={o.v}
                            type="button"
                            onClick={() => onChange({ position: o.v })}
                            data-testid={`${prefix}-position-${o.v}`}
                            className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${
                                cap.position === o.v
                                    ? "bg-red-600/20 border-red-600 text-white"
                                    : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                        >{o.t}</button>
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">Caption size</span>
                <input
                    type="range"
                    min={60}
                    max={300}
                    step={2}
                    value={activePx}
                    onChange={(e) => onChange({ sizePx: Number(e.target.value) })}
                    data-testid={`${prefix}-size-slider`}
                    className="flex-1 accent-red-600"
                />
                <span className="text-[10px] text-zinc-400 tabular-nums w-14 text-right">
                    {activePx}px · {pctOfWidth}%
                </span>
                {cap.sizePx ? (
                    <button
                        type="button"
                        onClick={() => onChange({ sizePx: null })}
                        data-testid={`${prefix}-size-reset`}
                        className="text-[10px] text-zinc-400 hover:text-white underline"
                    >reset</button>
                ) : null}
            </div>
        </div>
    );
}
